import { ColumnProps } from "antd/lib/table";
import { RequestHistory, RequestHistoryFilter } from "models/RequestHistory";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { LayoutCell, LayoutHeader, OneLineText } from "react3l-ui-library";
import nameof from "ts-nameof.macro";
import { filterService } from "services/page-services/filter-service";
import { Moment } from "moment";
import { formatDateTime } from "helpers/date-time";
import {
  APP_USER_PERMISSION_HISTORY_DETAIL_ROUTE,
} from "config/route-consts";
import { OrderType } from "react3l-common";
import { requestHistoryService } from "services/page-services/request-history-service";
import { useHistory } from "react-router-dom";
import { appUserRepository } from "repositories/app-user-repository";
import { View24, RecentlyViewed24 } from "@carbon/icons-react";
import { AppUser } from "models/AppUser";
import { formatNumber } from "helpers/number";

export function useUserPermissionHistoryListHook() {
  const [translate] = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [dataHistoryPreview, setDataHistoryPreview] = useState([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const { modelFilter: filter, dispatchFilter } = filterService.useModelFilter(
    RequestHistoryFilter,
    {
      orderBy: "savedAt",
      orderType: OrderType.DESC,
    }
  );

  const {
    requestHistoryList,
    requestHistoryCount,
    rowSelection,
    handleCompareHistory,
    handlePagination,
  } = requestHistoryService.useHistoryRequestList<
    RequestHistory,
    RequestHistoryFilter
  >(
    appUserRepository.listRequestHistory,
    appUserRepository.countRequestHistory,
    filter,
    dispatchFilter,
    APP_USER_PERMISSION_HISTORY_DETAIL_ROUTE,
    "versionId"
  );

  const history = useHistory();
  const handleGoBack = React.useCallback(() => {
    history.goBack();
  }, [history]);

  const previewHistory = React.useCallback((requestHistory: any) => {
    setIsSpinning(true);
    appUserRepository.getRequestHistory(requestHistory?.versionId)
      .subscribe({
        next: (res: AppUser) => {
          setIsVisible(true);
          setDataHistoryPreview(res?.content?.appUserRoles);
          setIsSpinning(false);
        },
        error: () => {
          setIsSpinning(false);
        },
      });
  }, [])

  const handleCloseModal = React.useCallback(() => {
    setIsVisible(false);
  }, [])

  const showHistoryDifferentChange = React.useCallback((versionId1, versionId2) => {
    history.push({
      pathname: APP_USER_PERMISSION_HISTORY_DETAIL_ROUTE,
      search: `?versionTo=${versionId1}&versionFrom=${versionId2}&different`,
    });
  }, [history])

  const columns: ColumnProps<any>[] = React.useMemo(
    () => [
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("appUsers.histories.appUser")}
          />
        ),
        key: nameof("actor"),
        dataIndex: nameof("actor"),
        ellipsis: true,
        render(actor: string) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={actor} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("appUsers.histories.action")}
          />
        ),
        key: nameof("actionName"),
        dataIndex: nameof("actionName"),
        ellipsis: true,
        render(actionName: string) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={actionName} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("appUsers.histories.savedAt")}
          />
        ),
        key: nameof("savedAt"),
        dataIndex: nameof("savedAt"),
        ellipsis: true,
        render(...params: [Moment, RequestHistory, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={formatDateTime(params[0])} />
            </LayoutCell>
          );
        },
      },

      {
        title: (
          <LayoutHeader orderType="left" title={translate("general.action")} />
        ),
        key: "action",
        dataIndex: "action",
        fixed: "right",
        width: 110,
        render(id: number, requestHistory: RequestHistory, index: number) {
          return (
            <div className="d-flex button-action-table missions-dxg">
              <div className="d-flex">
                <div className="enable-dxg">
                  <View24 className="btn-missions-green" onClick={() => previewHistory(requestHistory)} />
                </div>
                {requestHistory.versionId
                  //Check điều kiện phân trang < 10, thì thằng cuối cùng sẽ bị disabled vì không có bản ghi nào để so sánh
                  && ((requestHistoryCount < 10 && filter.skip === undefined
                    && requestHistoryList.indexOf(requestHistoryList[requestHistoryList.length - 1]) === index)
                    //Check điều kiện phân trang > 10, thì thằng cuối cùng sẽ bị disabled vì không có bản ghi nào để so sánh
                    || (requestHistoryCount > 10 && filter.skip === (requestHistoryCount - requestHistoryList.length)
                      && requestHistoryList.indexOf(requestHistoryList[requestHistoryList.length - 1]) === index))
                  ? <div className="disable-dxg ml-2">
                    <RecentlyViewed24 />
                  </div>
                  : <div className="enable-dxg ml-2">
                    <RecentlyViewed24 className="btn-missions-blue" onClick={() =>
                      showHistoryDifferentChange(requestHistory.versionId, requestHistoryList[index + 1]?.versionId)} />
                  </div>}
              </div>
            </div>
          );
        },
      },
    ],
    [translate, previewHistory, requestHistoryList, showHistoryDifferentChange, filter.skip, requestHistoryCount]
  );

  const columnsModalHistoryPreview: ColumnProps<AppUser>[] = React.useMemo(
    () => [
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("general.columns.index")}
          />
        ),
        width: 50,
        ellipsis: true,
        key: "stt",
        dataIndex: "stt",
        render(...params: [number, AppUser, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText
                value={formatNumber(params[2] + 1)}
                countCharacters={50}
              />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("appUsers.roles.code")}
          />
        ),
        width:150,
        key: "roleCode",
        dataIndex: "role",
        ellipsis: true,
        render(...params: [any, AppUser, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={params[1]?.role?.code} countCharacters={50} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("appUsers.roles.name")}
          />
        ),
        width:150,
        key: "roleName",
        dataIndex: "role",
        ellipsis: true,
        render(...params: [any, AppUser, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={params[1]?.role?.name} countCharacters={50} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("appUsers.roles.type")}
          />
        ),
        width:100,
        key: "assignmentType",
        dataIndex: "assignmentType",
        ellipsis: true,
        render(...params: [any, AppUser, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={params[0]?.name} countCharacters={50} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("appUsers.roles.subSystem")}
          />
        ),
        width:100,
        key: "roleSubSystem",
        dataIndex: "role",
        render(...params: [any, AppUser, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={params[1]?.role?.site?.name} countCharacters={50} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("appUsers.roles.condition")}
          />
        ),
        width:100,
        key: "appUserResource",
        dataIndex: "appUserResource",
        render(...params: [any, AppUser, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={params[0]?.fieldEntity?.name} countCharacters={50} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("appUsers.roles.operationCode")}
          />
        ),
        width:80,
        key: "appUserResourceOperationCode",
        dataIndex: "appUserResource",
        render(...params: [any, AppUser, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={params[1]?.appUserResource?.permissionOperator?.name} countCharacters={50} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("appUsers.roles.value")}
          />
        ),
        key: "appUserResourceValue",
        dataIndex: "appUserResource",
        width:200,
        render(...params: [any, AppUser, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText
                // value={params[1]?.appUserResource?.masterData
                //   ? params[1]?.appUserResource?.masterData?.code + " _ " + params[1]?.appUserResource?.masterData?.name
                //   : ""}
                value={
                  params[1]?.appUserResource?.masterDatas
                    ? params[1]?.appUserResource?.masterDatas?.map((item: any) => item.name)?.toString()
                    : ""
                }
                countCharacters={50} />
            </LayoutCell>
          );
        },
      },
    ],
    [translate]
  );

  return {
    translate,
    handleCompareHistory,
    handleGoBack,
    handlePagination,
    requestHistoryList,
    requestHistoryCount,
    rowSelection,
    columns,
    filter,
    columnsModalHistoryPreview,
    isVisible,
    dataHistoryPreview,
    handleCloseModal,
    isSpinning,
  };
}
